// Design agent: Streamlines the inspector with shared helpers and consistent palette usage.
// Developer agent: De-duplicates normalisation and gradient logic to simplify future changes.
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  findOverlaps,
  formatMask,
  formatWildcard,
  getAddressMetadata,
  getAllSubnetMasks,
  getIpv4Class,
  getPrefixFromMask,
  getTotalAddressCount,
  intToIpv4,
  ipv4ToInt,
  longestPrefixMatch,
  parseCidr,
  parseRange,
} from '../utils/ipMath';
import { normaliseWithTail } from '../utils/listNormalization';
import { createBitGradientStyle } from '../utils/bitStyling';
import { isValidInputFormat } from '../utils/ipMath';
import { useKeyboardLayout } from '../utils/useKeyboardLayout';
import { toBinaryDotted } from '../utils/exporters';
import { IpInputField } from './IpInputField';
import { BorderTrail } from './BorderTrail';
import OverallSummaryCard from './OverallSummaryCard';
import { Alert, AlertTitle, AlertDescription } from './Alert';
import HoverableAddress from './HoverableAddress';
import ExportMenu from './ExportMenu';
import { ExternalIntel, PublicAddressPanel } from './ExternalIntel';


// Design agent: Defines the chromatic palette used for network and host highlights.
const INSPECTOR_COLORS = {
  network: '#4F46E5',
  host: '#F97316',
};

// Design agent: Shares accent colours with the hierarchical planner for consistent theming.
// Developer agent: Centralises palette references used when syncing UI states.
const INSPECTOR_COLOR_B = 'var(--layer-color-b)';
const INSPECTOR_COLOR_C = 'var(--layer-color-c)';

const MAX_INSPECTOR_FIELDS = 24;

// Design agent: Spec 013 — column order/labels for the inspector's CSV/TSV export, matching the
// spec's "Inspector CSV columns" list exactly so the exported sheet reads the same regardless of
// export format (Download CSV vs. Copy for spreadsheet).
const INSPECTOR_CSV_HEADERS = [
  'Entry',
  'Input',
  'Network',
  'Prefix',
  'Mask',
  'Wildcard',
  'Broadcast',
  'First host',
  'Last host',
  'Usable hosts',
  'Class',
  'Type',
  'Binary (dotted)',
];

// Design agent: Palette for Belong Mode visualization (matches Hierarchical Method 2)
const BELONG_ARROW_COLORS = [
  'rgb(88, 80, 231)',
  'rgb(135, 146, 249)',
  'rgb(67, 193, 249)',
];

// Design agent: Configures the shared normaliser so the inspector maintains progressive fields.
// Developer agent: Ensures a single trailing blank entry regardless of user edits.
const INSPECTOR_FIELD_NORMALISER = {
  maxItems: MAX_INSPECTOR_FIELDS,
  createEmpty: () => '',
  cleanValue: (value) => (typeof value === 'string' ? value : ''),
  isEmpty: (value) => value.trim() === '',
};

// Design agent: Anticipates adjacent networks to help the user hop through sequential ranges.
// Developer agent: Calculates up to three valid follow-up network addresses for the active CIDR.
const buildNextNetworks = (parsed) => {
  if (!parsed) {
    return [];
  }

  const blockSize = 2 ** Math.max(0, 32 - parsed.prefix);
  if (blockSize <= 0) {
    return [];
  }

  const next = [];
  let candidate = parsed.network + blockSize;

  for (let index = 0; index < 3; index += 1) {
    if (candidate > 0xFFFFFFFF) {
      break;
    }
    next.push(`${intToIpv4(candidate)}/${parsed.prefix}`);
    candidate += blockSize;
  }

  return next;
};

// Design agent: Computes contiguous free ranges around the inspected IP.
const computeFreeSegments = (ipInt, usableStart, usableEnd) => {
  if (usableEnd < usableStart) {
    return { left: 0, right: 0 };
  }
  const clampedIp = Math.min(Math.max(ipInt, usableStart), usableEnd);
  return {
    left: Math.max(0, clampedIp - usableStart),
    right: Math.max(0, usableEnd - clampedIp),
  };
};

// Design agent: Builds a colour-aware description for each octet of an address.
// Developer agent: Emits deterministic segment entries so memoised views remain stable.
const buildColorisedOctets = (address, prefix, colors) => {
  const octets = address.split('.');
  const fullNetworkOctets = Math.floor(prefix / 8);
  const partialBits = prefix % 8;

  return octets.flatMap((octet, index) => {
    const segments = [];
    if (index < fullNetworkOctets) {
      segments.push({
        key: `octet-${index}`,
        value: octet,
        style: { color: colors.network },
        role: 'network',
      });
    } else if (index === fullNetworkOctets && partialBits > 0) {
      const bitSlice = [];
      for (let bit = 0; bit < 8; bit += 1) {
        bitSlice.push(bit < partialBits ? colors.network : colors.host);
      }
      segments.push({
        key: `octet-${index}`,
        value: octet,
        style: createBitGradientStyle(bitSlice, colors.host),
        role: 'mixed',
      });
    } else {
      segments.push({
        key: `octet-${index}`,
        value: octet,
        style: { color: colors.host },
        role: 'host',
      });
    }

    if (index < octets.length - 1) {
      segments.push({
        key: `dot-${index}`,
        value: '.',
        style: { color: 'inherit' },
        role: 'separator',
      });
    }
    return segments;
  });
};

// Design agent: Produces colour details for the boundary octet when it is split by the mask.
const buildBoundaryBinary = (address, prefix, colors) => {
  const partialBits = prefix % 8;
  if (partialBits === 0) {
    return null;
  }
  const boundaryOctetIndex = Math.floor(prefix / 8);
  const octetValue = Number(address.split('.')[boundaryOctetIndex]);
  const binary = octetValue.toString(2).padStart(8, '0');
  const wholePrefix = Math.floor(prefix / 8) * 8;
  const cidrEquation = `/${prefix} = ${wholePrefix} + ${partialBits}`;
  return {
    networkBits: binary.slice(0, partialBits),
    hostBits: binary.slice(partialBits),
    colors,
    octetIndex: boundaryOctetIndex,
    hostBitCount: 8 - partialBits,
    networkBitCount: partialBits,
    cidrEquation,
  };
};

// Design agent: Extracts the dotted suffix that represents the host side of an address.
const getHostPortionLabel = (addressInt, prefix) => {
  const octets = intToIpv4(addressInt).split('.');
  const hostBits = Math.max(0, 32 - prefix);
  if (hostBits <= 0) {
    return `.${octets[octets.length - 1]}`;
  }
  const suffixStart = Math.min(octets.length, Math.floor(prefix / 8));
  const suffixOctets = octets.slice(suffixStart);
  const suffix = suffixOctets.length === 0 ? octets[octets.length - 1] : suffixOctets.join('.');
  return `.${suffix}`;
};

// Design agent: Derives interpreted metrics for a specific IPv4 entry to drive its insight card.
// Developer agent: Normalises computed values so the presentation layer can remain stateless.
const buildInsightAnalysis = (rawValue) => {
  const trimmed = rawValue.trim();

  if (trimmed === '') {
    return {
      parsed: null,
      prompt: 'Enter an IPv4 address with CIDR (e.g. 192.168.1.1/24) or a mask (e.g. /24 or 255.255.255.0).',
      isHostOnly: false,
    };
  }

  // Design agent: Detect explicit subnet mask queries to show the mask reference table.
  // Developer agent: Checks for both short CIDR (e.g. /24) and full dotted-decimal masks.
  const isCidrShort = trimmed.startsWith('/');
  let maskInsightPrefix = null;

  if (isCidrShort) {
    const prefix = Number(trimmed.slice(1));
    if (!Number.isNaN(prefix) && prefix >= 0 && prefix <= 32) {
      maskInsightPrefix = prefix;
    }
  } else {
    // Check if the input is a valid dotted-decimal mask
    const ipInt = ipv4ToInt(trimmed);
    if (ipInt !== null && !trimmed.includes('/')) {
      maskInsightPrefix = getPrefixFromMask(ipInt);
    }
  }

  if (maskInsightPrefix !== null) {
    const allMasks = getAllSubnetMasks();
    const targetRow = allMasks.find((m) => m.cidr === maskInsightPrefix);
    return {
      parsed: null,
      isMaskInsight: true,
      targetPrefix: maskInsightPrefix,
      allMasks,
      targetRow,
      colors: INSPECTOR_COLORS,
    };
  }

  // Design agent: Range detection logic - integrated into general analysis
  const rangeParsed = parseRange(trimmed);
  if (rangeParsed) {
    // Design agent: Calculate markers and visuals for range insight
    const markers = [];
    const rangeSize = rangeParsed.rangeEndInt - rangeParsed.rangeStartInt;

    // Helper to calculate percentage position on the track
    const getPositionPercent = (ipInt) => {
      if (rangeSize === 0) return 50;
      return ((ipInt - rangeParsed.rangeStartInt) / rangeSize) * 100;
    };

    // Calculate user range visual properties
    const userRangeStartPercent = getPositionPercent(rangeParsed.userStartInt);
    const userRangeEndPercent = getPositionPercent(rangeParsed.userEndInt);
    const userRangeWidthPercent = Math.max(0, userRangeEndPercent - userRangeStartPercent);

    // Add markers
    markers.push({
      type: 'user-start',
      position: userRangeStartPercent,
      ip: rangeParsed.userStartIp,
      label: 'USER START',
      color: 'var(--md-on-background)',
      zIndex: 10,
    });

    markers.push({
      type: 'user-end',
      position: userRangeEndPercent,
      ip: rangeParsed.userEndIp,
      label: 'USER END',
      color: 'var(--md-on-background)',
      zIndex: 10,
    });

    if (rangeParsed.hasStartCidr && rangeParsed.startNetwork !== null) {
      if (rangeParsed.startNetwork >= rangeParsed.rangeStartInt && rangeParsed.startNetwork <= rangeParsed.rangeEndInt) {
        markers.push({
          type: 'network',
          position: getPositionPercent(rangeParsed.startNetwork),
          ip: intToIpv4(rangeParsed.startNetwork),
          label: `NET /${rangeParsed.startPrefix}`,
          color: 'var(--md-primary)',
          zIndex: 5,
        });
      }
      if (rangeParsed.startBroadcast >= rangeParsed.rangeStartInt &&
        rangeParsed.startBroadcast <= rangeParsed.rangeEndInt &&
        rangeParsed.startBroadcast !== rangeParsed.startNetwork) {
        markers.push({
          type: 'broadcast',
          position: getPositionPercent(rangeParsed.startBroadcast),
          ip: intToIpv4(rangeParsed.startBroadcast),
          label: `BRD /${rangeParsed.startPrefix}`,
          color: 'var(--md-tertiary)',
          zIndex: 5,
        });
      }
    }

    if (rangeParsed.hasEndCidr && rangeParsed.endNetwork !== null) {
      const startNetInt = rangeParsed.hasStartCidr ? rangeParsed.startNetwork : null;
      const startBrdInt = rangeParsed.hasStartCidr ? rangeParsed.startBroadcast : null;

      if (rangeParsed.endNetwork >= rangeParsed.rangeStartInt &&
        rangeParsed.endNetwork <= rangeParsed.rangeEndInt &&
        rangeParsed.endNetwork !== startNetInt) {
        markers.push({
          type: 'network',
          position: getPositionPercent(rangeParsed.endNetwork),
          ip: intToIpv4(rangeParsed.endNetwork),
          label: `NET /${rangeParsed.endPrefix}`,
          color: 'var(--md-primary)',
          zIndex: 5,
        });
      }

      if (rangeParsed.endBroadcast >= rangeParsed.rangeStartInt &&
        rangeParsed.endBroadcast <= rangeParsed.rangeEndInt &&
        rangeParsed.endBroadcast !== startBrdInt &&
        rangeParsed.endBroadcast !== rangeParsed.endNetwork) {
        markers.push({
          type: 'broadcast',
          position: getPositionPercent(rangeParsed.endBroadcast),
          ip: intToIpv4(rangeParsed.endBroadcast),
          label: `BRD /${rangeParsed.endPrefix}`,
          color: 'var(--md-tertiary)',
          zIndex: 5,
        });
      }
    }

    return {
      parsed: null,
      rangeParsed: rangeParsed,
      isRangeInsight: true,
      totalAddresses: rangeParsed.totalAddresses,
      markers,
      visuals: {
        userStartPercent: userRangeStartPercent,
        userWidthPercent: userRangeWidthPercent,
      },
      colors: INSPECTOR_COLORS,
    };
  }

  let parsed = parseCidr(trimmed);
  if (!parsed) {
    const ipIntOnly = ipv4ToInt(trimmed);
    if (ipIntOnly === null) {
      return {
        parsed: null,
        error: 'Invalid IPv4 or CIDR value. Try 192.168.1.10/24.',
        isHostOnly: false,
      };
    }
    parsed = {
      ip: trimmed,
      prefix: 32,
      mask: 0xffffffff,
      network: ipIntOnly,
      broadcast: ipIntOnly,
    };
  }

  const ipInt = ipv4ToInt(parsed.ip);
  if (ipInt === null) {
    return {
      parsed: null,
      error: 'The IPv4 portion is not valid.',
      isHostOnly: false,
    };
  }

  const isHostOnly = parsed.prefix === 32;
  const totalAddresses = getTotalAddressCount(parsed.prefix);
  const metadata = getAddressMetadata(ipInt);
  const mask = formatMask(parsed.mask);
  const wildcard = formatWildcard(parsed.mask);
  const clampedIp = Math.min(Math.max(ipInt, parsed.network), parsed.broadcast);
  // Design agent: Extract host mask to isolate host bits for proportional placement.
  const hostMask = (~parsed.mask) >>> 0;
  const hostRangeStart = parsed.prefix >= 31 ? parsed.network : parsed.network + 1;
  const hostRangeEnd = parsed.prefix >= 31 ? parsed.broadcast : parsed.broadcast - 1;
  // Design agent: Count only usable hosts so the slider represents actual assignable endpoints.
  const usableHostCount = hostRangeEnd < hostRangeStart ? 0 : hostRangeEnd - hostRangeStart + 1;
  const clampedHostAddress =
    usableHostCount <= 0
      ? hostRangeStart
      : Math.min(Math.max(clampedIp, hostRangeStart), hostRangeEnd);
  const hostIndex = usableHostCount <= 0 ? 0 : clampedHostAddress - hostRangeStart;
  const hostProgressRatio = usableHostCount <= 1 ? 0 : hostIndex / (usableHostCount - 1);
  const hostProgressPercent = hostProgressRatio * 100;
  const totalRange = parsed.broadcast - parsed.network;
  // Design agent: Map usable host spans back onto the visual track to preserve network/broadcast context.
  const hostWindowStartRatio =
    totalRange === 0 ? 0 : Math.max(0, Math.min(1, (hostRangeStart - parsed.network) / totalRange));
  const hostWindowEndRatio =
    totalRange === 0 ? 1 : Math.max(0, Math.min(1, (hostRangeEnd - parsed.network) / totalRange));
  const hostWindowWidthRatio = Math.max(0, hostWindowEndRatio - hostWindowStartRatio);
  // Design agent: Preserve host-space placement for interaction feedback.
  const hostIndicatorRatio =
    hostWindowWidthRatio === 0
      ? hostWindowStartRatio
      : hostWindowStartRatio + hostProgressRatio * hostWindowWidthRatio;
  // Design agent: Position the indicator using only the host bits so equivalent host values align across networks.
  const hostSegment = clampedIp & hostMask;
  const networkTrackRatio = hostMask === 0 ? 0 : Math.max(0, Math.min(1, hostSegment / hostMask));
  // Design agent: Clamp proportional positioning to the track bounds to avoid overflow.
  const indicatorTrackPercent = Math.min(100, Math.max(0, networkTrackRatio * 100));
  const hostWindowStartPercent = hostWindowStartRatio * 100;
  const hostWindowWidthPercent = hostWindowWidthRatio * 100;
  const hostFillPercent =
    hostWindowWidthRatio === 0 ? 0 : hostWindowWidthPercent * hostProgressRatio;
  // Design agent: Spec 001 — the gauge now paints a single continuous orange -> red gradient across
  // the whole host span (left = network / first host, right = broadcast / last host) instead of the
  // previous position-dependent cutoff. This removes the double colour system that used to overlap
  // with the violet/teal host-window overlay and makes the fill read as one coherent gauge.
  const trackGradient = 'linear-gradient(90deg, var(--layer-host-color) 0%, var(--layer-host-color-end) 100%)';
  const freeSegments = computeFreeSegments(ipInt, hostRangeStart, hostRangeEnd);
  const isNetworkAddress = ipInt === parsed.network;
  const isBroadcastAddress = ipInt === parsed.broadcast;
  const colorisedIp = buildColorisedOctets(parsed.ip, parsed.prefix, INSPECTOR_COLORS);
  const boundaryBinary = buildBoundaryBinary(parsed.ip, parsed.prefix, INSPECTOR_COLORS);
  const hostPortionNetwork = getHostPortionLabel(parsed.network, parsed.prefix);
  const hostPortionBroadcast = getHostPortionLabel(parsed.broadcast, parsed.prefix);
  const hostPortionCurrent = getHostPortionLabel(ipInt, parsed.prefix);
  const indicatorAccent = isNetworkAddress
    ? INSPECTOR_COLOR_B
    : isBroadcastAddress
      ? INSPECTOR_COLOR_C
      : INSPECTOR_COLORS.host;
  const hostBits = Math.max(0, 32 - parsed.prefix);
  // Design agent: Report the usable host count so UI metrics align with the progress bar.
  const totalHosts = parsed.prefix >= 31 ? totalAddresses : Math.max(0, totalAddresses - 2);
  const indicatorScale = Math.max(64, Math.min(240, 48 + hostBits * 12));
  const indicatorTextColor = isNetworkAddress
    ? INSPECTOR_COLOR_B
    : isBroadcastAddress
      ? INSPECTOR_COLOR_C
      : INSPECTOR_COLORS.host;

  return {
    parsed,
    prefix: parsed.prefix,
    mask,
    wildcard,
    type: metadata.label,
    addressMetadata: metadata,
    ipClass: getIpv4Class(parsed.ip),
    hostProgressPercent,
    hostProgressRatio,
    indicatorTrackPercent,
    indicatorTrackRatio: networkTrackRatio,
    hostIndicatorRatio,
    hostWindowStartPercent,
    hostWindowWidthPercent,
    hostFillPercent,
    trackGradient,
    usableHostCount,
    hostIndex,
    networkAddress: intToIpv4(parsed.network),
    broadcastAddress: intToIpv4(parsed.broadcast),
    // Design agent: Spec 003 needs full dotted addresses for the first and last usable host so each
    // can be wrapped in a HoverableAddress binary-breakdown popover, not just the host-range counts
    // already used to size the gauge.
    firstHostAddress: intToIpv4(hostRangeStart),
    lastHostAddress: intToIpv4(hostRangeEnd),
    leftUsable: freeSegments.left,
    rightUsable: freeSegments.right,
    inspectedIp: parsed.ip,
    totalHosts,
    colorisedIp,
    boundaryBinary,
    hostPortionNetwork,
    hostPortionBroadcast,
    hostPortionCurrent,
    isNetworkAddress,
    isBroadcastAddress,
    indicatorAccent,
    indicatorTextColor,
    indicatorScale,
    colors: INSPECTOR_COLORS,
    currentOctet: hostPortionCurrent,
    nextNetworks: buildNextNetworks(parsed),
    isHostOnly,
  };
};

// Design agent: Renders an IPv4 insight summary card directly beneath its entry field.
// Developer agent: Encapsulates tooltip interactions while delegating collapse state to the parent list.
function InspectorCard({
  analysis,
  entryIndex,
  canCollapse,
  isCollapsed,
  onToggle,
  // Design agent: Spec 005 — minimal plumbing so this card can flag that its entry overlaps with
  // another one, without the header needing to know how the overlap was found.
  // Developer agent: Both default to "nothing to show" so every other call site (and any future
  // one) keeps working unchanged if it does not pass these two props.
  overlapLabel = null,
  isHighlighted = false,
}) {
  const [isProgressHovered, setIsProgressHovered] = useState(false);
  // Design agent: Stores live tooltip metadata for the progress bar.
  const [tooltipState, setTooltipState] = useState({
    isVisible: false,
    x: 0,
    y: 0,
    label: '',
    side: 'before',
  });
  // Design agent: References the progress track for tooltip alignment.
  const progressTrackRef = useRef(null);

  // Design agent: Guarantees unique DOM ids for repeated insight sections.
  // Developer agent: Keeps id construction declarative for easier maintenance.
  const buildId = (suffix) => `ipv4-insight-entry-${entryIndex}-${suffix}`;

  // Design agent: Shows tooltips when the progress bar is hovered.
  const handleProgressEnter = () => {
    setIsProgressHovered(true);
    setTooltipState((prev) => ({
      ...prev,
      isVisible: true,
    }));
  };

  // Design agent: Hides tooltips when the pointer leaves the progress bar.
  const handleProgressLeave = () => {
    setIsProgressHovered(false);
    setTooltipState((prev) => ({
      ...prev,
      isVisible: false,
    }));
  };

  // Design agent: Positions a dynamic tooltip relative to the pointer.
  const handleProgressMove = (event) => {
    if (!progressTrackRef.current || !analysis.parsed) {
      return;
    }
    const rect = progressTrackRef.current.getBoundingClientRect();
    const relativeX = event.clientX - rect.left;
    const relativeY = event.clientY - rect.top;
    const ratio = rect.width === 0 ? 0 : Math.max(0, Math.min(relativeX / rect.width, 1));
    const side = ratio <= analysis.hostIndicatorRatio ? 'before' : 'after';
    const label =
      side === 'before'
        ? `Number of hosts before: ${analysis.leftUsable.toLocaleString()}`
        : `Number of hosts after: ${analysis.rightUsable.toLocaleString()}`;

    setTooltipState({
      isVisible: true,
      x: relativeX,
      y: relativeY,
      label,
      side,
    });
  };

  // Design agent: Drive the progress visuals directly from the computed host-based ratios.
  const activeIndicatorPercent = analysis.parsed ? analysis.indicatorTrackPercent : 0;
  const activeHostFillPercent = analysis.parsed ? analysis.hostFillPercent : 0;
  const hostWindowStartPercent = analysis.parsed ? analysis.hostWindowStartPercent : 0;
  const hostWindowWidthPercent = analysis.parsed ? analysis.hostWindowWidthPercent : 0;
  // Design agent: Inline gradient keeps the track colours aligned with the computed proportions.
  const progressTrackStyle = analysis.parsed
    ? { background: analysis.trackGradient, backgroundRepeat: 'no-repeat' }
    : undefined;
  // Design agent: Shift the indicator so edge cases snap cleanly to the track boundaries.
  const indicatorTransform =
    activeIndicatorPercent <= 0
      ? 'translate(0, -50%)'
      : activeIndicatorPercent >= 100
        ? 'translate(-100%, -50%)'
        : 'translate(-50%, -50%)';

  return (
    <div
      className={`result-card insight-card${analysis.parsed && canCollapse ? ' insight-card--collapsible' : ''}${isCollapsed ? ' is-collapsed' : ''}${isHighlighted ? ' is-overlap-highlighted' : ''}`}
      id={buildId('card')}
    >
      <div className="insight-card-header" id={buildId('card-header')}>
        <div className="insight-card-header-text" id={buildId('card-header-text')}>
          <span className="insight-card-label" id={buildId('card-label')}>
            IPv4 Insight
          </span>
          <span className="insight-card-summary" id={buildId('card-summary')}>
            {analysis.parsed
              ? `Inspecting ${analysis.inspectedIp}/${analysis.prefix}`
              : analysis.error
                ? 'Invalid IPv4/CIDR input'
                : analysis.prompt
                  ? analysis.prompt
                  : 'Enter an IPv4 / CIDR value'}
          </span>
          {/* Design agent: Spec 005 — a small chip so an admin scanning this card alone still
              learns it collides with another entry, instead of only finding out from the
              Overall Summary card further down the page. */}
          {/* Developer agent: overlapLabel is a pre-built string (see IpInspector's
              overlapLabelsByIndex) so this card stays unaware of how overlaps are computed. */}
          {overlapLabel && (
            <span className="insight-card-overlap-chip" id={buildId('overlap-chip')}>
              {overlapLabel}
            </span>
          )}
        </div>
        {analysis.parsed && canCollapse && (
          <button
            type="button"
            className="insight-card-toggle"
            id={buildId('card-toggle')}
            onClick={onToggle}
            aria-expanded={!isCollapsed}
          >
            {isCollapsed ? 'Expand' : 'Collapse'}
          </button>
        )}
      </div>
      <div className="insight-card-body" id={buildId('card-body')} aria-hidden={isCollapsed}>
        {analysis.error && <p className="error-text" id={buildId('card-error')}>{analysis.error}</p>}
        {!analysis.parsed && !analysis.error && analysis.prompt && (
          <p className="result-summary" id={buildId('card-prompt')}>
            {analysis.prompt}
          </p>
        )}
        {/* Design agent: Limits host-only insights to the essentials for single-address checks. */}
        {/* Developer agent: Avoids rendering progress metrics when the network range collapses to one host. */}
        {analysis.parsed && analysis.isHostOnly && (
          <div className="insight-grid insight-grid--compact" id={buildId('host-only-grid')}>
            <div id={buildId('type-summary')}>
              <p className="insight-title" id={buildId('type-title')}>
                Type
              </p>
              <p id={buildId('type-value')}>{analysis.type}</p>
              {analysis.addressMetadata?.category === 'multicast' &&
                analysis.addressMetadata.service && (
                  <p className="insight-meta" id={buildId('multicast-service')}>
                    Service: {analysis.addressMetadata.service}
                  </p>
                )}
            </div>
            <div id={buildId('class-summary')}>
              <p className="insight-title" id={buildId('class-title')}>
                Class
              </p>
              <p id={buildId('class-value')}>{analysis.ipClass}</p>
            </div>
          </div>
        )}
        {analysis.parsed && !analysis.isHostOnly && (
          <>
            <div className="cidr-display" aria-label="Inspected address" id={buildId('cidr-display')}>
              <span className="cidr-prefix" id={buildId('cidr-prefix')}>
                Inspecting
              </span>
              {/* Design agent: Spec 003 — hovering/focusing the inspected address reveals its full
                  binary breakdown, so the coloured octets keep their existing markup nested inside
                  the new HoverableAddress wrapper. */}
              <HoverableAddress
                id={buildId('cidr-octets-hoverable')}
                address={analysis.inspectedIp}
                prefix={analysis.prefix}
                networkColor={analysis.colors.network}
                hostColor={analysis.colors.host}
                label="Inspected address"
              >
                <span className="cidr-octets" id={buildId('cidr-octets')}>
                  {analysis.colorisedIp.map((segment) => (
                    <span
                      key={segment.key}
                      className="cidr-segment"
                      id={buildId(`cidr-segment-${segment.key}`)}
                      style={segment.style}
                      data-role={segment.role}
                    >
                      {segment.value}
                    </span>
                  ))}
                </span>
              </HoverableAddress>
              <span className="cidr-separator" id={buildId('cidr-separator')}>/</span>
              <span
                className="cidr-segment"
                id={buildId('cidr-prefix-value')}
                style={{ color: analysis.colors.network }}
              >
                {analysis.prefix}
              </span>
            </div>

            <div className="progress-wrapper" id={buildId('progress-wrapper')}>
              <div
                ref={progressTrackRef}
                className="progress-track"
                id={buildId('progress-track')}
                style={progressTrackStyle}
                onMouseEnter={handleProgressEnter}
                onMouseLeave={handleProgressLeave}
                onMouseMove={handleProgressMove}
              >
                <div
                  className="progress-host-window"
                  id={buildId('progress-host-window')}
                  style={{
                    left: `${hostWindowStartPercent}%`,
                    width: `${hostWindowWidthPercent}%`,
                  }}
                />
                <div
                  className="progress-fill"
                  id={buildId('progress-fill')}
                  style={{
                    left: `${hostWindowStartPercent}%`,
                    width: `${activeHostFillPercent}%`,
                  }}
                />
                <div
                  className={`progress-indicator ${isProgressHovered ? 'is-hovered' : ''}`}
                  id={buildId('progress-indicator')}
                  style={{
                    left: `${activeIndicatorPercent}%`,
                    transform: indicatorTransform,
                    borderColor: analysis.indicatorAccent,
                  }}
                >
                  <span
                    id={buildId('progress-indicator-value')}
                    style={{ color: analysis.indicatorTextColor }}
                  >
                    {analysis.currentOctet}
                  </span>
                </div>
              </div>
              {tooltipState.isVisible && (
                <div
                  className={`progress-hover-tooltip progress-hover-tooltip--${tooltipState.side}`}
                  style={{
                    left: `${tooltipState.x}px`,
                    top: `${tooltipState.y}px`,
                  }}
                  id={buildId('progress-tooltip')}
                >
                  {tooltipState.label}
                </div>
              )}
              <div className="progress-extents" id={buildId('progress-extents')}>
                <span
                  className="endpoint"
                  id={buildId('endpoint-network')}
                  style={{ color: INSPECTOR_COLOR_B }}
                >
                  {analysis.prefix === 31 ? 'FIRST: ' : 'NET: '}
                  {/* Design agent: Spec 003 — the visible label stays the abbreviated host suffix
                      (unchanged), but the popover it reveals always shows the full network address
                      in binary, since that full value is what a learner needs to decompose. */}
                  <HoverableAddress
                    id={buildId('endpoint-network-hoverable')}
                    address={analysis.networkAddress}
                    prefix={analysis.prefix}
                    networkColor={analysis.colors.network}
                    hostColor={analysis.colors.host}
                    label={analysis.prefix === 31 ? 'First address' : 'Network address'}
                  >
                    {analysis.hostPortionNetwork}
                  </HoverableAddress>
                </span>
                <span
                  className="endpoint endpoint--right"
                  id={buildId('endpoint-broadcast')}
                  style={{ color: INSPECTOR_COLOR_C }}
                >
                  {analysis.prefix === 31 ? 'SECOND: ' : 'BRD: '}
                  <HoverableAddress
                    id={buildId('endpoint-broadcast-hoverable')}
                    address={analysis.broadcastAddress}
                    prefix={analysis.prefix}
                    networkColor={analysis.colors.network}
                    hostColor={analysis.colors.host}
                    label={analysis.prefix === 31 ? 'Second address' : 'Broadcast address'}
                  >
                    {analysis.hostPortionBroadcast}
                  </HoverableAddress>
                </span>
              </div>
            </div>

            {/* Design agent: Spec 002 — cite the RFC that defines /31 point-to-point links so the
                reader can verify the rule instead of taking the note on faith. */}
            {analysis.prefix === 31 && (
              <Alert id={buildId('rfc3021-note')}>
                <AlertTitle>RFC 3021 Compliance</AlertTitle>
                <AlertDescription>
                  <p>
                    Both addresses of this /31 are usable hosts (no network or broadcast address is
                    reserved), per{' '}
                    <a
                      href="https://www.rfc-editor.org/rfc/rfc3021"
                      target="_blank"
                      rel="noreferrer noopener"
                      id={buildId('rfc3021-link')}
                    >
                      RFC 3021
                    </a>
                    .
                  </p>
                  <p>Some specific hardware systems do not support /31 links.</p>
                </AlertDescription>
              </Alert>
            )}

            <div className="insight-grid" id={buildId('insight-grid')}>
              {analysis.boundaryBinary && (
                <div className="boundary-panel" id={buildId('boundary-panel')}>
                  <p className="insight-title" id={buildId('boundary-title')}>
                    {`Boundary octet ${analysis.boundaryBinary.octetIndex + 1}`}
                  </p>
                  <p className="binary-octet" id={buildId('boundary-bits')}>
                    <span style={{ color: analysis.colors.network }}>{analysis.boundaryBinary.networkBits}</span>
                    <span style={{ color: analysis.colors.host }}>{analysis.boundaryBinary.hostBits}</span>
                  </p>
                  <div className="boundary-guide" aria-hidden="true" id={buildId('boundary-equation')}>
                    {(() => {
                      const eq = analysis.boundaryBinary.cidrEquation;
                      const plusIndex = eq.indexOf('+');
                      if (plusIndex === -1) {
                        return (
                          <span className="boundary-count" id={buildId('boundary-count')}>
                            {eq}
                          </span>
                        );
                      }
                      const before = eq.slice(0, plusIndex + 1);
                      const after = eq.slice(plusIndex + 1).trim();
                      return (
                        <span className="boundary-count" id={buildId('boundary-count-split')}>
                          <span id={buildId('boundary-count-before')}>{before} </span>
                          <span
                            id={buildId('boundary-count-after')}
                            style={{ color: analysis.colors.network }}
                          >
                            {after}
                          </span>
                        </span>
                      );
                    })()}
                  </div>
                </div>
              )}
              <div id={buildId('total-hosts')}>
                <p className="insight-title" id={buildId('total-hosts-title')}>
                  Total hosts
                </p>
                <p id={buildId('total-hosts-value')}>
                  {analysis.totalHosts.toLocaleString()}
                </p>
              </div>
              {/* Design agent: Spec 003 — the first and last usable host were previously only used
                  to size the gauge, never shown as text. /31 links already surface both endpoints
                  as FIRST/SECOND above, so these two cells only add value for prefixes below /31. */}
              {analysis.prefix < 31 && (
                <div id={buildId('first-host-summary')}>
                  <p className="insight-title" id={buildId('first-host-title')}>
                    First host
                  </p>
                  <p id={buildId('first-host-value')}>
                    <HoverableAddress
                      id={buildId('first-host-hoverable')}
                      address={analysis.firstHostAddress}
                      prefix={analysis.prefix}
                      networkColor={analysis.colors.network}
                      hostColor={analysis.colors.host}
                      label="First host"
                    >
                      {analysis.firstHostAddress}
                    </HoverableAddress>
                  </p>
                </div>
              )}
              {analysis.prefix < 31 && (
                <div id={buildId('last-host-summary')}>
                  <p className="insight-title" id={buildId('last-host-title')}>
                    Last host
                  </p>
                  <p id={buildId('last-host-value')}>
                    <HoverableAddress
                      id={buildId('last-host-hoverable')}
                      address={analysis.lastHostAddress}
                      prefix={analysis.prefix}
                      networkColor={analysis.colors.network}
                      hostColor={analysis.colors.host}
                      label="Last host"
                    >
                      {analysis.lastHostAddress}
                    </HoverableAddress>
                  </p>
                </div>
              )}
              <div id={buildId('mask-summary')}>
                <p className="insight-title" id={buildId('mask-title')}>
                  Mask
                </p>
                <p id={buildId('mask-value')}>{analysis.mask}</p>
              </div>
              <div id={buildId('wildcard-summary')}>
                <p className="insight-title" id={buildId('wildcard-title')}>
                  Wildcard
                </p>
                <p id={buildId('wildcard-value')}>{analysis.wildcard}</p>
              </div>
              <div id={buildId('type-summary')}>
                <p className="insight-title" id={buildId('type-title')}>
                  Type
                </p>
                <p id={buildId('type-value')}>{analysis.type}</p>
                {analysis.addressMetadata?.category === 'multicast' &&
                  analysis.addressMetadata.service && (
                    <p className="insight-meta" id={buildId('multicast-service')}>
                      Service: {analysis.addressMetadata.service}
                    </p>
                  )}
              </div>
              <div id={buildId('class-summary')}>
                <p className="insight-title" id={buildId('class-title')}>
                  Class
                </p>
                <p id={buildId('class-value')}>{analysis.ipClass}</p>
              </div>
              <div id={buildId('next-networks')}>
                <p className="insight-title" id={buildId('next-networks-title')}>
                  Next networks
                </p>
                {analysis.nextNetworks.length > 0 ? (
                  <ul className="insight-list" id={buildId('next-networks-list')}>
                    {analysis.nextNetworks.map((network, index) => (
                      <li key={network} id={buildId(`next-network-${index}`)}>
                        {network}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="insight-meta" id={buildId('next-networks-empty')}>
                    No further networks within range.
                  </p>
                )}
              </div>
            </div>
          </>
        )}

        {/* Design agent: Spec 014 — the collapsed External lookups section only makes sense for a
            public address (RFC1918/special-use ranges have nothing a third-party whois/ASN lookup
            could usefully add), so it is gated on the same category the rest of the app already
            derives from the address metadata catalogue. */}
        {analysis.parsed && analysis.addressMetadata?.category === 'public' && (
          <ExternalIntel ip={analysis.inspectedIp} idPrefix={buildId('external-intel')} />
        )}

        {/* Design agent: Displays a reference table for subnet masks when a mask input is detected. */}
        {analysis.isMaskInsight && (
          <div className="mask-insight-container" id={buildId('mask-insight')}>
            <p className="mask-insight-highlight" id={buildId('mask-highlight')}>
              Your mask is:{' '}
              <span style={{ color: analysis.colors.network, fontWeight: 700 }}>
                {analysis.targetRow.mask} &nbsp; {analysis.targetRow.wildcard} &nbsp; /{analysis.targetRow.cidr}
              </span>
            </p>
            <div className="mask-table-wrapper">
              <div className="mask-table-header">
                <span>Mask</span>
                <span>Wildcard</span>
                <span>CIDR</span>
              </div>
              <div className="mask-table-body">
                {analysis.allMasks.map((row) => {
                  const isTarget = row.cidr === analysis.targetPrefix;
                  return (
                    <div
                      key={row.cidr}
                      className="mask-table-row"
                      style={isTarget ? { color: analysis.colors.network, fontWeight: 600, background: 'rgba(var(--md-sys-color-primary-rgb), 0.1)' } : {}}
                    >
                      <span>{row.mask}</span>
                      <span>{row.wildcard}</span>
                      <span>/{row.cidr}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Design agent: Renders a dedicated card for Range Insights (IP - IP).
function RangeInsightCard({ analysis }) {
  const [tooltipState, setTooltipState] = useState({
    isVisible: false,
    x: 0,
    y: 0,
    label: '',
  });
  const progressTrackRef = useRef(null);
  const { rangeParsed, markers, visuals, totalAddresses } = analysis;

  const handleProgressEnter = () => {
    setTooltipState((prev) => ({ ...prev, isVisible: true }));
  };

  const handleProgressLeave = () => {
    setTooltipState((prev) => ({ ...prev, isVisible: false }));
  };

  const handleProgressMove = (event) => {
    if (!progressTrackRef.current) {
      return;
    }
    const rect = progressTrackRef.current.getBoundingClientRect();
    const relativeX = event.clientX - rect.left;
    const relativeY = event.clientY - rect.top;
    const ratio = rect.width === 0 ? 0 : Math.max(0, Math.min(relativeX / rect.width, 1));

    // Calculate the IP at the current position
    const rangeSize = rangeParsed.rangeEndInt - rangeParsed.rangeStartInt;
    const currentOffset = Math.round(ratio * rangeSize);
    const currentIpInt = rangeParsed.rangeStartInt + currentOffset;
    const currentIp = intToIpv4(currentIpInt);

    // Count hosts from start to current position
    const hostsFromStart = currentOffset + 1;

    setTooltipState({
      isVisible: true,
      x: relativeX,
      y: relativeY,
      label: `${currentIp} | ${hostsFromStart.toLocaleString()} from start`,
    });
  };

  return (
    <div className="result-card insight-card range-insight-card" id="range-insight-card">
      <div className="insight-card-header" id="range-insight-header">
        <div className="insight-card-header-text">
          <span className="insight-card-label">Range Insight</span>
          <span className="insight-card-summary">
            {rangeParsed.userStartIp} → {rangeParsed.userEndIp}
            {rangeParsed.wasSwapped && ' (auto-corrected)'}
          </span>
        </div>
      </div>

      <div className="insight-card-body" id="range-insight-body">
        {/* Design agent: Main metric - total addresses in range */}
        <div className="range-metric-hero" id="range-metric-hero">
          <span className="range-metric-value">{totalAddresses.toLocaleString()}</span>
          <span className="range-metric-label">Total addresses</span>
        </div>

        {/* Design agent: Progress bar / timeline visualization */}
        <div className="progress-wrapper" id="range-progress-wrapper">
          <div
            ref={progressTrackRef}
            className="progress-track range-progress-track"
            id="range-progress-track"
            onMouseEnter={handleProgressEnter}
            onMouseLeave={handleProgressLeave}
            onMouseMove={handleProgressMove}
          >
            {/* Design agent: The full visual range is displayed as the track background (neutral) */}
            <div className="range-progress-base" style={{ width: '100%', position: 'absolute', top: 0, bottom: 0, opacity: 0.1, background: 'var(--md-on-surface)' }} />

            {/* Design agent: The user-selected range is highlighted */}
            <div
              className="range-progress-fill"
              style={{
                left: `${visuals.userStartPercent}%`,
                width: `${visuals.userWidthPercent}%`
              }}
            />

            {/* Design agent: Markers */}
            {markers && markers.map((marker, index) => (
              <div
                key={`marker-${index}-${marker.type}`}
                className={`range-marker range-marker--${marker.type}`}
                style={{
                  left: `${marker.position}%`,
                  borderColor: marker.color,
                  zIndex: marker.zIndex || 2,
                }}
                title={`${marker.label}: ${marker.ip}`}
              >
                <span className="range-marker-dot" style={{ background: marker.color }} />
              </div>
            ))}
          </div>

          {tooltipState.isVisible && (
            <div
              className="progress-hover-tooltip"
              style={{
                left: `${tooltipState.x}px`,
                top: `${tooltipState.y}px`,
              }}
              id="range-progress-tooltip"
            >
              {tooltipState.label}
            </div>
          )}

          <div className="progress-extents" id="range-progress-extents">
            <span className="endpoint range-endpoint-start">
              START: {rangeParsed.rangeStartIp}
            </span>
            <span className="endpoint endpoint--right range-endpoint-end">
              END: {rangeParsed.rangeEndIp}
            </span>
          </div>
        </div>

        {/* Design agent: Network details displayed as premium distinct cards */}
        {(rangeParsed.hasStartCidr || rangeParsed.hasEndCidr) && (
          <div className="range-network-grid" id="range-network-details">
            {rangeParsed.hasStartCidr && (
              <div className="range-detail-card range-detail-card--start">
                <div className="range-detail-header">
                  <span className="part-indicator indicator-start" />
                  <span className="range-detail-title">Start Network</span>
                  <span className="range-detail-badge">/{rangeParsed.startPrefix}</span>
                </div>
                <div className="range-detail-content">
                  <div className="detail-row">
                    <span className="detail-label">Network</span>
                    <span className="detail-value">{intToIpv4(rangeParsed.startNetwork)}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Broadcast</span>
                    <span className="detail-value">{intToIpv4(rangeParsed.startBroadcast)}</span>
                  </div>
                </div>
              </div>
            )}

            {rangeParsed.hasEndCidr && (
              <div className="range-detail-card range-detail-card--end">
                <div className="range-detail-header">
                  <span className="part-indicator indicator-end" />
                  <span className="range-detail-title">End Network</span>
                  <span className="range-detail-badge">/{rangeParsed.endPrefix}</span>
                </div>
                <div className="range-detail-content">
                  <div className="detail-row">
                    <span className="detail-label">Network</span>
                    <span className="detail-value">{intToIpv4(rangeParsed.endNetwork)}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Broadcast</span>
                    <span className="detail-value">{intToIpv4(rangeParsed.endBroadcast)}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// Design agent: Component exposing IPv4 insights with per-entry panels and Route Summarizer behaviour.
// Developer agent: Drives an editable list of IPv4/CIDR inputs, each backed by an independent insight card.
// Design agent: Checks if a raw IP integer falls within a parsed network range.
const isIpInSubnet = (ipInt, networkParsed) => {
  if (!networkParsed || networkParsed.prefix === 32) return false;
  return ipInt >= networkParsed.network && ipInt <= networkParsed.broadcast;
};

// Design agent: Spec 006 — finds every valid network a belonging host address falls into, plus
// which of those a real router would actually pick (the longest / most specific prefix). Shared
// by the arrow overlay (BelongArrows, which also needs DOM element refs to draw with) and the
// plain-text "Belongs to..." result line, so both agree on the exact same set of matches.
// Developer agent: Deliberately has no DOM dependency — it only reads from `entries`/`analyses`,
// so the text result can be computed synchronously during render, unlike BelongArrows' own
// geometry pass which must wait for layout via useEffect/getBoundingClientRect.
const computeBelongMatches = (leftIndex, entries, analyses, belongingIndices) => {
  const leftAnalysis = analyses[leftIndex];
  if (!leftAnalysis || !leftAnalysis.parsed) {
    return { matches: [], bestIndices: [] };
  }
  const leftIpInt = ipv4ToInt(leftAnalysis.parsed.ip);
  const matches = [];
  entries.forEach((_, rightIndex) => {
    if (belongingIndices.includes(rightIndex)) {
      return;
    }
    const rightAnalysis = analyses[rightIndex];
    if (!rightAnalysis || !rightAnalysis.parsed || rightAnalysis.isHostOnly) {
      return;
    }
    if (leftIpInt !== null && isIpInSubnet(leftIpInt, rightAnalysis.parsed)) {
      matches.push({ index: rightIndex, parsed: rightAnalysis.parsed });
    }
  });
  if (matches.length === 0) {
    return { matches, bestIndices: [] };
  }
  const { bestIndices: bestPositions } = longestPrefixMatch(leftIpInt, matches.map((match) => match.parsed));
  const bestIndices = bestPositions.map((position) => matches[position].index);
  return { matches, bestIndices };
};

// Design agent: Turns a computeBelongMatches result into the "Belongs to N networks — routed to
// X (longest prefix)" sentence from spec 006. A single match keeps a plain, unqualified sentence
// since there is no routing decision to explain in that case.
// Developer agent: Ties (several networks sharing the longest prefix) are called out explicitly
// per spec 006 item 3, listing every tied network rather than picking one arbitrarily.
const buildBelongResultText = (matchSummary) => {
  if (!matchSummary || matchSummary.matches.length === 0) {
    return 'Does not belong to any entered network';
  }
  if (matchSummary.matches.length === 1) {
    const onlyMatch = matchSummary.matches[0].parsed;
    return `Belongs to ${intToIpv4(onlyMatch.network)}/${onlyMatch.prefix}`;
  }
  const bestLabels = matchSummary.bestIndices.map((entryIndex) => {
    const bestMatch = matchSummary.matches.find((match) => match.index === entryIndex);
    return `${intToIpv4(bestMatch.parsed.network)}/${bestMatch.parsed.prefix}`;
  });
  const tieNote = bestLabels.length > 1 ? ', tie' : '';
  return `Belongs to ${matchSummary.matches.length} networks — routed to ${bestLabels.join(', ')} (longest prefix${tieNote})`;
};

// Design agent: Component exposing IPv4 insights with per-entry panels and Route Summarizer behaviour.
// Developer agent: Drives an editable list of IPv4/CIDR inputs, each backed by an independent insight card.
// Design agent: Spec 003 — accepts an optional onActivePrefixChange callback so a sibling view (the
// Reference table) can highlight the prefix currently being inspected, without the inspector needing
// to know anything about who is listening or why.
// Design agent: Spec 011 — accepts an optional `initialState` ({entries, belongingIndices}, exactly
// the shape src/utils/persistence.js decodes) so App can restore a reload/shared link, and an
// `onStateChange` callback fired whenever that same shape changes so App can persist it. Both are
// optional so IpInspector keeps working unchanged when mounted standalone (e.g. in isolated tests).
function IpInspector({ onActivePrefixChange, initialState, onStateChange } = {}) {
  // Design agent: Seeds the inspector with the restored entries when present, otherwise the single
  // empty entry every fresh visit already started with — both paths go through the same shared
  // normaliser so the trailing-blank-row invariant holds either way.
  // Developer agent: Uses lazy initialisation (the function form of useState) so the restore/parse
  // work only ever runs once, on mount, not on every render.
  const [entries, setEntries] = useState(() => {
    const restoredEntries = initialState?.entries;
    const seed = Array.isArray(restoredEntries) && restoredEntries.length > 0 ? restoredEntries : [''];
    return normaliseWithTail(seed, INSPECTOR_FIELD_NORMALISER);
  });

  // Initialize keyboard layout detection
  useKeyboardLayout();

  const inputRefs = useRef([]);
  // Design agent: Tracks collapsed panels so keyboard automation can tuck away previous insights.
  // Developer agent: Stores indices in an array to preserve predictable order when expanding/collapsing.
  const [collapsedEntries, setCollapsedEntries] = useState([]);
  // Design agent: Memorises which input currently holds focus for automatic collapse.
  // Developer agent: Keeps the index in state so effects can react consistently to focus shifts.
  const [activeEntryIndex, setActiveEntryIndex] = useState(-1);

  // Design agent: Track indices that are in "Belonging" mode (Left Column). Spec 011 — restored from
  // initialState so a reload/shared link reopens with the same addresses in the same column.
  const [belongingIndices, setBelongingIndices] = useState(() =>
    Array.isArray(initialState?.belongingIndices) ? initialState.belongingIndices : []
  );

  // Design agent: Spec 005 — the pair of entry indices currently pointed at by hovering an
  // overlap row in the Overall Summary card, so their input fields (and insight cards) can be
  // outlined elsewhere on the page.
  // Developer agent: Lives here (rather than inside OverallSummaryCard) because the fields it
  // needs to outline are rendered by this component, in both the normal and belong-mode layouts.
  const [highlightedEntryIndices, setHighlightedEntryIndices] = useState([]);

  // Design agent: Highlights both fields of an overlapping pair on hover.
  const handleHighlightEntries = (indices) => {
    setHighlightedEntryIndices(indices);
  };

  // Design agent: Clears the highlight once the pointer leaves the overlap row.
  const handleClearHighlightedEntries = () => {
    setHighlightedEntryIndices([]);
  };

  // Design agent: Coordinates focus and scroll adjustments so transitions feel fluid.
  // Developer agent: Prefers requestAnimationFrame to prevent synchronous layout reads.
  const scheduleNextFrame = (callback) => {
    if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(callback);
      return;
    }
    setTimeout(callback, 0);
  };

  // Design agent: Compute live insight data for every entry while caching between renders.
  // Developer agent: Keeps expensive IPv4 math within memoisation bounds for performance.
  const analyses = useMemo(() => {
    return entries.map((value) => buildInsightAnalysis(value));
  }, [entries]);

  // Design agent: Spec 005 — names every overlapping (or duplicate) pair of entries so the chip
  // on each insight card can say exactly which other entry it collides with.
  // Developer agent: findOverlaps is fed the full entries list (null for empty/invalid rows) so
  // the returned a/b positions already match the 1-based entry numbers shown throughout the UI.
  const overlapPairs = useMemo(() => {
    const parsedByIndex = analyses.map((analysis) => (analysis.parsed ? analysis.parsed : null));
    return findOverlaps(parsedByIndex);
  }, [analyses]);

  // Design agent: Reduces the raw overlap pairs into one ready-to-render chip label per entry
  // index, so the JSX for each insight card stays a single prop lookup.
  // Developer agent: An entry can appear in more than one pair (overlapping with several others),
  // so every match is joined into a single chip rather than rendering one chip per pair.
  const overlapLabelsByIndex = useMemo(() => {
    const labels = {};
    const appendLabel = (index, text) => {
      labels[index] = labels[index] ? `${labels[index]}, ${text}` : text;
    };
    overlapPairs.forEach((pair) => {
      const entryNumberA = pair.a + 1;
      const entryNumberB = pair.b + 1;
      if (pair.isDuplicate) {
        appendLabel(pair.a, `duplicate of #${entryNumberB}`);
        appendLabel(pair.b, `duplicate of #${entryNumberA}`);
      } else {
        appendLabel(pair.a, `overlaps with #${entryNumberB}`);
        appendLabel(pair.b, `overlaps with #${entryNumberA}`);
      }
    });
    return labels;
  }, [overlapPairs]);

  // Design agent: Tallies valid IPv4 rows, treating bare hosts as acceptable targets for insight cards.
  // Developer agent: Reads directly from the source entries so thresholds mirror the latest user edits.
  const validEntryCount = useMemo(() => {
    return entries.reduce((count, value) => {
      const trimmed = value.trim();
      if (trimmed === '') {
        return count;
      }
      if (parseCidr(trimmed)) {
        return count + 1;
      }
      return ipv4ToInt(trimmed) !== null ? count + 1 : count;
    }, 0);
  }, [entries]);

  // Design agent: Enables collapse controls as soon as two or more insights are populated.
  // Developer agent: Shares the flag with each entry to keep render conditions simple.
  const isCollapseEnabled = validEntryCount >= 2;

  // Design agent: Spec 013 — the Export menu needs the same information already shown on each
  // insight card, just flattened into one CSV/TSV row per successfully parsed entry, so exporting
  // never disagrees with what the user sees on screen.
  // Developer agent: Skips entries that are empty, invalid, a mask-reference lookup, or a range
  // insight — none of those have the network/prefix/broadcast shape this row format assumes.
  const inspectorExportRows = useMemo(() => {
    return analyses.reduce((rows, analysis, index) => {
      if (!analysis.parsed) {
        return rows;
      }
      rows.push([
        index + 1,
        entries[index].trim(),
        analysis.networkAddress,
        analysis.prefix,
        analysis.mask,
        analysis.wildcard,
        analysis.broadcastAddress,
        analysis.firstHostAddress,
        analysis.lastHostAddress,
        analysis.usableHostCount,
        analysis.ipClass,
        analysis.type,
        toBinaryDotted(analysis.inspectedIp),
      ]);
      return rows;
    }, []);
  }, [analyses, entries]);

  // Design agent: Spec 013 — feeds the "Copy BIND records" export mode. Every entry becomes its own
  // "subnet" named `entry` (the inspector has no user-supplied subnet name the way the VLSM planner
  // does, so `{name}-{index}` naturally becomes `entry-1`, `entry-2`, ...). Host-only /32 entries are
  // left out: their first and last usable host are the same single address, so a BIND record for
  // them would just duplicate itself under two hostnames for no benefit.
  // Developer agent: Converts the dotted first/last host strings back to integers with ipv4ToInt
  // (already imported for other purposes in this file) so toBindRecords never has to parse strings.
  const inspectorBindSubnets = useMemo(() => {
    return analyses.reduce((subnets, analysis, index) => {
      if (!analysis.parsed || analysis.prefix >= 32) {
        return subnets;
      }
      subnets.push({
        name: 'entry',
        network: analysis.parsed.network,
        prefix: analysis.prefix,
        firstHost: ipv4ToInt(analysis.firstHostAddress),
        lastHost: ipv4ToInt(analysis.lastHostAddress),
      });
      return subnets;
    }, []);
  }, [analyses]);

  // Design agent: Spec 014 — "Inspect it" on the discovered public address should behave like the
  // user just typed that address themselves: it lands in the first free entry field (the normaliser
  // already keeps one trailing blank entry available) rather than replacing whatever the user was
  // already inspecting.
  // Developer agent: Falls back to appending a new entry only if every existing slot is already
  // filled (bounded by MAX_INSPECTOR_FIELDS via normaliseWithTail, same as every other entry edit).
  const handleInspectPublicAddress = (ip) => {
    setEntries((previous) => {
      const next = [...previous];
      const emptyIndex = next.findIndex((value) => value.trim() === '');
      if (emptyIndex !== -1) {
        next[emptyIndex] = ip;
      } else {
        next.push(ip);
      }
      return normaliseWithTail(next, INSPECTOR_FIELD_NORMALISER);
    });
  };

  // Design agent: Reports the prefix of the first inspected entry (the primary, most prominent
  // card) so the Reference table can highlight the matching row. Multi-entry inspection stays a
  // purely local concern of this component; only the leading entry's prefix is shared upward.
  // Developer agent: Guards against a missing callback so IpInspector still works standalone (e.g.
  // in isolated tests) without a parent wiring this prop.
  useEffect(() => {
    if (typeof onActivePrefixChange !== 'function') {
      return;
    }
    const firstAnalysis = analyses[0];
    onActivePrefixChange(firstAnalysis && firstAnalysis.parsed ? firstAnalysis.prefix : null);
  }, [analyses, onActivePrefixChange]);

  // Design agent: Spec 011 — reports the persistable slice of this component's own state (the
  // entries list and which of them are in "Belonging" mode) every time either changes, so App can
  // debounce a write to localStorage/the URL without IpInspector knowing anything about persistence.
  // Developer agent: Guards against a missing callback the same way the effect above does, so
  // IpInspector keeps working unchanged when mounted without a parent wiring this prop.
  useEffect(() => {
    if (typeof onStateChange !== 'function') {
      return;
    }
    onStateChange({ entries, belongingIndices });
  }, [entries, belongingIndices, onStateChange]);

  // Design agent: Collapses a specific insight card while keeping the state immutable.
  // Developer agent: Avoids duplicate entries when collapsing the same index repeatedly.
  const collapseEntry = (index) => {
    if (index < 0) {
      return;
    }
    setCollapsedEntries((previous) => {
      if (previous.includes(index)) {
        return previous;
      }
      return [...previous, index];
    });
  };

  // Design agent: Allows manual toggling from the insight card header button.
  // Developer agent: Switches between collapsed and expanded states in a single handler.
  const toggleEntryCollapse = (index) => {
    setCollapsedEntries((previous) => {
      return previous.includes(index)
        ? previous.filter((item) => item !== index)
        : [...previous, index];
    });
  };

  // Design agent: Automatically collapses the previous panel when focus advances to the next input.
  // Developer agent: Reacts to focus changes so both keyboard and pointer navigation behave identically.
  useEffect(() => {
    if (!isCollapseEnabled || activeEntryIndex <= 0) {
      return;
    }
    const previousIndex = activeEntryIndex - 1;
    const previousAnalysis = analyses[previousIndex];
    if (!previousAnalysis || !previousAnalysis.parsed) {
      return;
    }
    collapseEntry(previousIndex);
  }, [activeEntryIndex, analyses, isCollapseEnabled]);

  // Design agent: Clears collapsed state when collapse is disabled or insights disappear.
  // Developer agent: Keeps the array aligned with rendered cards to avoid orphaned indices.
  useEffect(() => {
    if (!isCollapseEnabled) {
      setCollapsedEntries([]);
      return;
    }
    setCollapsedEntries((previous) => {
      const filtered = previous.filter((index) => analyses[index]?.parsed);
      return filtered.length === previous.length ? previous : filtered;
    });
  }, [analyses, isCollapseEnabled]);

  // Design agent: Moves the caret to a specific IPv4 field after auto-expansion.
  // Developer agent: Re-attempts focus until the next field exists to keep keyboard flow snappy.
  const focusEntryField = (index) => {
    const tryFocus = (attempt = 0) => {
      const element = inputRefs.current[index];
      if (element) {
        const length = element.value.length;
        try {
          element.focus({ preventScroll: true });
        } catch (error) {
          element.focus();
        }
        element.setSelectionRange(length, length);
        setActiveEntryIndex(index);
        return;
      }
      if (attempt < 5) {
        scheduleNextFrame(() => tryFocus(attempt + 1));
      }
    };

    tryFocus();
  };

  // Design agent: Propagates edits through the inspector entries while keeping them normalised.
  // Developer agent: Updates the entry list and advances focus when a valid CIDR is completed.
  const handleEntryChange = (index, value, caretPosition) => {
    // ip field - restriction
    const sanitized = value.replace(/[^0-9abcdef.:/ -]/gi, '');
    setEntries((previous) => {
      const next = [...previous];
      next[index] = sanitized;
      return normaliseWithTail(next, INSPECTOR_FIELD_NORMALISER);
    });
  };

  // Design agent: Mirrors Route Summarizer key behaviour for fast keyboard-driven input.
  // Developer agent: Navigates the entry list with Enter/Backspace shortcuts.
  const handleEntryKeyDown = (event, index) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      const nextIndex = Math.min(index + 1, entries.length - 1);
      focusEntryField(nextIndex);
      return;
    }

    if (event.key === 'Backspace' && entries[index].trim() === '' && index > 0) {
      event.preventDefault();
      focusEntryField(index - 1);
    }
  };

  // Design agent: Toggle functionality for Belong Mode.
  const handleToggleBelong = (index) => {
    setBelongingIndices(prev => {
      if (prev.includes(index)) {
        // If removing the currently selected index, clear selection
        if (selectedBelongIndex === index) {
          setSelectedBelongIndex(null);
        }
        return prev.filter(i => i !== index);
      }
      return [...prev, index];
    });
  };

  const [selectedBelongIndex, setSelectedBelongIndex] = useState(null);
  const [hoveredBelongIndex, setHoveredBelongIndex] = useState(null);

  const handleBelongInputClick = (index) => {
    setSelectedBelongIndex(prev => prev === index ? null : index);
  };

  const cleanBelongSelection = () => {
    setSelectedBelongIndex(null);
    setHoveredBelongIndex(null);
  }

  const clearBelongMode = () => {
    setBelongingIndices([]);
    cleanBelongSelection();
  };

  const isBelongMode = belongingIndices.length > 0;

  // Design agent: Render a single entry (Used by both columns).
  // Developer agent: Extracted to avoid duplication and maintain consistent ref/key usage.
  const renderEntry = (index) => {
    const value = entries[index];
    const analysis = analyses[index];
    const isBelonging = belongingIndices.includes(index);

    return (

      <div
        className="insight-entry"
        id={`ipv4-insight-entry-${index}`}
        key={`ipv4-insight-entry-${index}`}
        data-index={index} // Helper for arrow positioning
      >
        {/* ip field */}
        <IpInputField
          ref={(element) => {
            inputRefs.current[index] = element;
            mainInputRefs.current[index] = element;
          }}
          index={index}
          value={value}
          isActive={activeEntryIndex === index}
          onChange={(val, caret) => handleEntryChange(index, val, caret)}
          onKeyDown={(event) => handleEntryKeyDown(event, index)}
          onFocus={() => setActiveEntryIndex(index)}
          onBlur={() => setActiveEntryIndex(-1)}
          showFormatHint={index === 0}
          isHighlighted={highlightedEntryIndices.includes(index)}
          // Design agent: Spec 010 — the shared list-normaliser stops appending a fresh trailing
          // field once the entries array hits MAX_INSPECTOR_FIELDS, so past that point Enter would
          // no longer add a line; the hint chip should not claim otherwise.
          isAtFieldLimit={entries.length >= MAX_INSPECTOR_FIELDS}
        >
          {/* Belong Trigger Button */}
          {analysis.parsed && analysis.isHostOnly && !value.includes('/') && !isBelonging && (
            <button
              type="button"
              className="input-ghost-action"
              onClick={() => handleToggleBelong(index)}
            >
              Belong ?
            </button>
          )}
        </IpInputField>

        {(analysis.parsed || analysis.isMaskInsight) && (
          <InspectorCard
            analysis={analysis}
            entryIndex={index}
            canCollapse={isCollapseEnabled}
            isCollapsed={collapsedEntries.includes(index)}
            onToggle={() => {
              if (!isCollapseEnabled) {
                return;
              }
              toggleEntryCollapse(index);
            }}
            overlapLabel={overlapLabelsByIndex[index] || null}
            isHighlighted={highlightedEntryIndices.includes(index)}
          />
        )}
        {analysis.isRangeInsight && (
          <RangeInsightCard analysis={analysis} />
        )}
      </div>
    );
  };

  // Design agent: Ref to find the spatial-view parent for portal rendering.
  const portalTargetRef = useRef(null);

  // Design agent: Update portal target on mount.
  useEffect(() => {
    // Find the spatial-view container that wraps the tool-card
    const toolCard = document.getElementById('tool-card-inspector');
    if (toolCard) {
      portalTargetRef.current = toolCard.closest('.spatial-view');
    }
  }, []);

  // Design agent: Separate refs for belong inputs (left) and main panel inputs (right).
  const belongInputRefs = useRef([]);
  const mainInputRefs = useRef([]);

  // Design agent: Spec 006 — precomputes, for every belonging host, which networks it matches
  // and which one a router would actually pick, feeding the "Belongs to N networks — routed to
  // X (longest prefix)" result line shown in the aside panel.
  // Developer agent: Kept separate from BelongArrows' own (DOM-dependent) computation so the text
  // can render immediately without waiting on the arrow overlay's layout pass.
  const belongMatchSummaries = useMemo(() => {
    const summaries = {};
    belongingIndices.forEach((leftIndex) => {
      summaries[leftIndex] = computeBelongMatches(leftIndex, entries, analyses, belongingIndices);
    });
    return summaries;
  }, [entries, analyses, belongingIndices]);

  if (isBelongMode) {
    // Design agent: Create aside panel content
    const asideContent = (
      <aside className="belong-aside-panel">
        <div className="belong-aside-content">
          {entries.map((value, index) => {
            if (belongingIndices.includes(index)) {
              // Design agent: Spec 006 — surfaces the longest-prefix routing decision as plain
              // text right under the host address, so the result is legible even without
              // following the arrows across the page.
              const belongResultText = buildBelongResultText(belongMatchSummaries[index]);
              return (
                <div
                  className="belong-simple-entry"
                  id={`belong-entry-${index}`}
                  key={`belong-entry-${index}`}
                  data-index={index}
                >
                  <div
                    className={`belong-simple-input-wrapper ${selectedBelongIndex === index ? 'is-selected' : ''}${highlightedEntryIndices.includes(index) ? ' is-overlap-highlighted' : ''}`}
                    onClick={() => handleBelongInputClick(index)}
                    onMouseEnter={() => setHoveredBelongIndex(index)}
                    onMouseLeave={() => setHoveredBelongIndex(null)}
                    style={{ borderLeft: `4px solid ${BELONG_ARROW_COLORS[index % BELONG_ARROW_COLORS.length]}` }}
                  >
                    <input
                      ref={(element) => {
                        belongInputRefs.current[index] = element;
                      }}
                      id={`belong-input-${index}`}
                      value={value}
                      className="field-input belong-simple-input"
                      readOnly
                      autoComplete="off"
                      style={{ pointerEvents: 'none' }}
                    />
                    <button
                      type="button"
                      className="belong-remove-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggleBelong(index);
                      }}
                      title="Remove from analysis"
                    >
                      ×
                    </button>
                  </div>
                  <p className="belong-result-text" id={`belong-result-text-${index}`}>
                    {belongResultText}
                  </p>
                </div>
              );
            }
            return null;
          })}
          <button className="belong-clear-all-button" onClick={clearBelongMode}>
            Clear All
          </button>
        </div>
      </aside>
    );

    return (
      <>
        {/* Portal the aside panel to the spatial-view level (outside tool-card) */}
        {portalTargetRef.current && createPortal(
          <div className="belong-portal-container">
            {asideContent}
          </div>,
          portalTargetRef.current
        )}

        {/* Arrow Overlay - relative to the spatial stage */}
        <BelongArrows
          entries={entries}
          analyses={analyses}
          belongingIndices={belongingIndices}
          belongInputRefs={belongInputRefs}
          mainInputRefs={mainInputRefs}
          overlayHostRef={portalTargetRef}
          selectedBelongIndex={selectedBelongIndex}
          hoveredBelongIndex={hoveredBelongIndex}
          onArrowClick={handleBelongInputClick}
          onArrowHover={setHoveredBelongIndex}
        />

        {/* Main Panel Content (inside tool-card as before) */}
        <div className="column-content" id="ipv4-insight-panel">
          <div className="field-group" id="ipv4-insight-field-group">
            <span className="field-group-label" id="ipv4-insight-field-label">
              IPv4 / CIDR entries
            </span>
            <div className="belong-target-column">
              {entries.map((value, index) => {
                if (!belongingIndices.includes(index)) {
                  const analysis = analyses[index];
                  return (
                    <div
                      className="insight-entry"
                      id={`ipv4-insight-entry-${index}`}
                      key={`ipv4-insight-entry-${index}`}
                      data-index={index}
                    >
                      <label
                        className="field"
                        htmlFor={`ipv4-insight-input-${index}`}
                        id={`ipv4-insight-label-${index}`}
                      >
                        <span id={`ipv4-insight-input-label-${index}`}>Field {index + 1}</span>
                        <BorderTrail
                          className={`field-input-wrapper${highlightedEntryIndices.includes(index) ? ' is-overlap-highlighted' : ''}`}
                          color={(activeEntryIndex === index && !isValidInputFormat(value) && !analysis.isRangeInsight && value.trim() !== '') ? "rgba(239, 68, 68, 0.8)" : "transparent"}
                          trailSize={100}
                        >
                          <input
                            ref={(element) => {
                              inputRefs.current[index] = element;
                              mainInputRefs.current[index] = element;
                            }}
                            id={`ipv4-insight-input-${index}`}
                            value={value}
                            placeholder="e.g. 10.0.0.125/24"
                            className="field-input"
                            onChange={(event) =>
                              handleEntryChange(index, event.target.value, event.target.selectionStart)
                            }
                            onKeyDown={(event) => handleEntryKeyDown(event, index)}
                            onFocus={() => setActiveEntryIndex(index)}
                            onBlur={() => setActiveEntryIndex(-1)}
                            autoComplete="off"
                            style={{ width: '100%' }}
                          />
                          {/* Belong Trigger Button */}
                          {analysis.parsed && analysis.isHostOnly && !value.includes('/') && (
                            <button
                              type="button"
                              className="input-ghost-action"
                              onClick={() => handleToggleBelong(index)}
                            >
                              Belong ?
                            </button>
                          )}
                        </BorderTrail>
                        {(() => {
                          const isFocused = activeEntryIndex === index;
                          const isValid = isValidInputFormat(value) || (analysis && analysis.isRangeInsight);
                          const isNotEmpty = value.trim() !== '';
                          if (!isFocused && !isValid && isNotEmpty) {
                            return (
                              <span className="error-text" style={{ fontSize: '0.8rem', marginTop: '0.25rem' }}>
                                invalid format
                              </span>
                            );
                          }
                          return null;
                        })()}
                      </label>
                      {(analysis.parsed || analysis.isMaskInsight) && (
                        <InspectorCard
                          analysis={analysis}
                          entryIndex={index}
                          canCollapse={isCollapseEnabled}
                          isCollapsed={collapsedEntries.includes(index)}
                          onToggle={() => {
                            if (!isCollapseEnabled) {
                              return;
                            }
                            toggleEntryCollapse(index);
                          }}
                          overlapLabel={overlapLabelsByIndex[index] || null}
                          isHighlighted={highlightedEntryIndices.includes(index)}
                        />
                      )}
                      {analysis.isRangeInsight && (
                        <RangeInsightCard analysis={analysis} />
                      )}
                    </div>
                  );
                }
                return null;
              })}
            </div>
          </div>
          {validEntryCount >= 2 && (
            <OverallSummaryCard
              analyses={analyses}
              onHighlightEntries={handleHighlightEntries}
              onClearHighlightedEntries={handleClearHighlightedEntries}
            />
          )}
        </div>

      </>
    );
  }

  return (
    <div className="column-content" id="ipv4-insight-panel">
      <div className="field-group" id="ipv4-insight-field-group">
        <div className="ipv4-insight-toolbar" id="ipv4-insight-toolbar">
          <span className="field-group-label" id="ipv4-insight-field-label">
            IPv4 / CIDR entries
          </span>
          <div className="ipv4-insight-toolbar-actions" id="ipv4-insight-toolbar-actions">
            {/* Design agent: Spec 014 — one "My public address" entry point for the whole inspector,
                since (unlike per-entry external lookups) there is only ever one visitor address to
                discover. Filling the result into a new entry reuses the same handler every other
                entry edit goes through. */}
            <PublicAddressPanel
              idPrefix="ipv4-insight-public-address"
              onInspectAddress={handleInspectPublicAddress}
            />
            {/* Design agent: Spec 013 — one Export menu for the whole inspector panel (not one per
                entry), since the CSV format already has an "entry" column to tell rows apart. */}
            <ExportMenu
              id="ipv4-insight-export-menu"
              filenamePrefix="masqip-inspector"
              csvHeaders={INSPECTOR_CSV_HEADERS}
              csvRows={inspectorExportRows}
              bindSubnets={inspectorBindSubnets}
            />
          </div>
        </div>
        {entries.map((_, index) => renderEntry(index))}
      </div>
      {validEntryCount >= 2 && (
        <OverallSummaryCard
          analyses={analyses}
          onHighlightEntries={handleHighlightEntries}
          onClearHighlightedEntries={handleClearHighlightedEntries}
        />
      )}
    </div>
  );
}

// Design agent: Dedicated component to draw SVG arrows connecting belonging IPs to their parent networks.
// Developer agent: Attaches the overlay to the spatial view so it scrolls with both columns.
function BelongArrows({ entries, analyses, belongingIndices, belongInputRefs, mainInputRefs, overlayHostRef, selectedBelongIndex, hoveredBelongIndex, onArrowClick, onArrowHover }) {
  const [arrowGroups, setArrowGroups] = useState([]);
  const [resizeCounter, setResizeCounter] = useState(0);
  const [overlayBox, setOverlayBox] = useState({ width: 0, height: 0 });
  const overlayHost = overlayHostRef?.current || null;

  useEffect(() => {
    if (!overlayHost) {
      setArrowGroups([]);
      return undefined;
    }

    const timer = setTimeout(() => {
      const hostRect = overlayHost.getBoundingClientRect();
      setOverlayBox({
        width: overlayHost.offsetWidth || hostRect.width,
        height: overlayHost.offsetHeight || hostRect.height,
      });

      const groups = {};

      belongingIndices.forEach((leftIndex) => {
        const leftEl = belongInputRefs.current[leftIndex];
        // Design agent: Spec 006 — reuses the shared, DOM-free match computation so the arrows
        // agree exactly with the plain-text "Belongs to..." result rendered in the aside panel.
        // Developer agent: `matches` already excludes /32 networks and belonging-mode hosts, so
        // only the DOM element lookup is left to do here.
        const { matches, bestIndices } = computeBelongMatches(leftIndex, entries, analyses, belongingIndices);
        if (!leftEl || matches.length === 0) {
          return;
        }

        const targets = matches
          .map((match) => ({ index: match.index, element: mainInputRefs.current[match.index] }))
          .filter((target) => target.element);

        if (targets.length > 0) {
          const leftWrapper = leftEl.closest('.belong-simple-input-wrapper') || leftEl.parentElement;
          const leftRect = leftWrapper.getBoundingClientRect();
          const startX = leftRect.right - hostRect.left;
          const startY = leftRect.top + leftRect.height / 2 - hostRect.top;

          const asidePanel = document.querySelector('.belong-aside-panel');
          let turnX;
          if (asidePanel) {
            const asideRect = asidePanel.getBoundingClientRect();
            turnX = asideRect.right - hostRect.left + 24;
          } else {
            turnX = startX + 40;
          }

          const branchPaths = [];
          let minY = startY;
          let maxY = startY;

          targets.forEach(target => {
            const rightRect = target.element.getBoundingClientRect();
            const endX = rightRect.left - hostRect.left;
            const endY = rightRect.top + rightRect.height / 2 - hostRect.top;

            minY = Math.min(minY, endY);
            maxY = Math.max(maxY, endY);

            // Design agent: Spec 006 — the longest-prefix (most specific) match is the one a
            // router would actually use, so it is flagged here to be drawn full-strength while
            // every other match on the same host stays visible but dimmed.
            branchPaths.push({
              d: `M ${turnX} ${endY} L ${endX} ${endY}`,
              isBest: bestIndices.includes(target.index),
            });
          });

          // Stem + Vertical Bus
          // Path: Start -> Turn -> (Retrace/Draw to Top) -> (Draw to Bottom)
          const stemPath = `M ${startX} ${startY} L ${turnX} ${startY} L ${turnX} ${minY} L ${turnX} ${maxY}`;

          groups[leftIndex] = {
            id: leftIndex,
            stemPath,
            branches: branchPaths,
          };
        }
      });

      setArrowGroups(Object.values(groups));
    }, 150);

    return () => clearTimeout(timer);
  }, [overlayHost, entries, analyses, belongingIndices, belongInputRefs, mainInputRefs, resizeCounter]);

  useEffect(() => {
    const handleViewportChanges = () => {
      setResizeCounter((prev) => prev + 1);
    };
    window.addEventListener('resize', handleViewportChanges);
    window.addEventListener('scroll', handleViewportChanges, true);
    return () => {
      window.removeEventListener('resize', handleViewportChanges);
      window.removeEventListener('scroll', handleViewportChanges, true);
    };
  }, []);

  if (!overlayHost || arrowGroups.length === 0) {
    return null;
  }

  // Sort groups: Active (Selected/Hovered) groups must be last to appear on top (higher z-index)
  // We use [...arrowGroups] to avoid mutating state directly
  const sortedGroups = [...arrowGroups].sort((a, b) => {
    const aActive = (selectedBelongIndex === a.id || hoveredBelongIndex === a.id) ? 1 : 0;
    const bActive = (selectedBelongIndex === b.id || hoveredBelongIndex === b.id) ? 1 : 0;
    return aActive - bActive;
  });

  return createPortal(
    <svg
      id="belong-arrows-overlay"
      className="belong-arrows-overlay-fixed"
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: overlayBox.width ? `${overlayBox.width}px` : '100%',
        height: overlayBox.height ? `${overlayBox.height}px` : '100%',
        pointerEvents: 'none',
        zIndex: 1000,
      }}
    >
      <defs>
        {BELONG_ARROW_COLORS.map((color, idx) => (
          <marker
            key={`marker-color-${idx}`}
            id={`arrowhead-belong-${idx}`}
            markerWidth="6"
            markerHeight="4"
            refX="5"
            refY="2"
            orient="auto"
          >
            <polygon points="0 0, 6 2, 0 4" fill={color} />
          </marker>
        ))}
      </defs>
      {sortedGroups.map((group) => {
        const isSelected = selectedBelongIndex === group.id;
        const isHovered = hoveredBelongIndex === group.id;
        const isActive = isSelected || isHovered;
        const color = BELONG_ARROW_COLORS[group.id % BELONG_ARROW_COLORS.length];

        return (
          <g
            key={`belong-group-${group.id}`}
            className={`belong-arrow-group ${isActive ? 'is-active' : ''}`}
          >
            <path
              d={group.stemPath}
              className="belong-arrow-path trunk"
              style={{ stroke: color }}
            />
            {group.branches.map((branch, i) => (
              <path
                key={`branch-${i}`}
                d={branch.d}
                // Design agent: Spec 006 — "is-best" gets full opacity and a thicker stroke (the
                // route a router would pick); every other match on this host is "is-dimmed" but
                // stays visible so the admin can still see every network the address falls into.
                className={`belong-arrow-path branch ${branch.isBest ? 'is-best' : 'is-dimmed'}`}
                markerEnd={`url(#arrowhead-belong-${group.id % BELONG_ARROW_COLORS.length})`}
                style={{ stroke: color }}
              />
            ))}
          </g>
        );
      })}
    </svg>,
    overlayHost
  );
}

// Design agent: Dedicated component for inspecting IP ranges.
// Developer agent: Provides a separate input field for range format (IP - IP or IP/CIDR - IP/CIDR).
// Design agent: Dedicated component for inspecting IP ranges.
// Developer agent: Provides a separate input field for range format (IP - IP or IP/CIDR - IP/CIDR).


export default IpInspector;
// Design agent: Also export named for import flexibility and IDE refactors.
export { IpInspector };
